import { useEffect } from "react";
import { clearCapturedCredentials } from "./apiClient";
import { signOut } from "aws-amplify/auth";
import { useQueryClient } from "@tanstack/react-query";
import type { PortalId } from "@livingsyncs/types";

/**
 * Base URL (scheme + host, no trailing slash) for each portal app. Used only
 * to NAME the correct portal in a wrong-portal error message below — each
 * app (frontend/portal, admin, auditor) hosts its own local sign-in, and a
 * user who signs in (or already has a session) for the wrong one is told
 * where to go instead, never silently carried over. Defaults are the real
 * preprod domains; docker-compose.yml overrides all three to localhost for
 * local dev.
 */
export const PORTAL_URL: Record<PortalId, string> = {
  app: import.meta.env.VITE_APP_URL || "https://portal.np.livingsyncs.com",
  admin: import.meta.env.VITE_ADMIN_URL || "https://admin.np.livingsyncs.com",
  audit:
    import.meta.env.VITE_AUDITOR_URL || "https://auditor.np.livingsyncs.com",
};

/** Human-facing name for each portal — also doubles as the "what app is
 * this" label each app's own LoginPage shows near the top. */
export const PORTAL_LABEL: Record<PortalId, string> = {
  app: "Portal",
  admin: "Admin Portal",
  audit: "Auditor Portal",
};

/** "This isn't your portal. Sign in at the Admin Portal (admin.np.livingsyncs.com) instead." */
export function wrongPortalMessage(home: PortalId): string {
  const host = PORTAL_URL[home].replace(/^https?:\/\//, "");
  return `This isn't your portal. Sign in at the ${PORTAL_LABEL[home]} (${host}) instead.`;
}

export type PortalGuardStatus =
  | "checking" // home not yet known
  | "own-portal" // render children
  | "no-access" // caller renders its local /no-access route
  | "wrong-portal"; // caller renders <Navigate to="/login" state={{ wrongPortalHome: home }} />

const WRONG_PORTAL_STORAGE_KEY = "ls.wrongPortalHome";

/**
 * Read (and clear) the wrong-portal home written by usePortalGuard below,
 * for LoginPage to fall back to. See usePortalGuard's doc for why router
 * state alone isn't reliable here. Cleared immediately on read so it can
 * never leak into an unrelated, later visit to /login.
 */
export function readAndClearWrongPortalHome(): PortalId | null {
  try {
    const value = sessionStorage.getItem(WRONG_PORTAL_STORAGE_KEY);
    sessionStorage.removeItem(WRONG_PORTAL_STORAGE_KEY);
    return (value as PortalId | null) ?? null;
  } catch {
    return null; // storage unavailable (e.g. private browsing) — no-op
  }
}

/**
 * Reconciles a resolved home portal against the app you're running in.
 * Handles the one non-trivial case: `home` is a REAL portal that isn't this
 * one — e.g. a role change moved this account to a different portal after
 * they already had a session here.
 *
 * `PrivateRoute` (components/PrivateRoute.tsx) independently listens for
 * the same Amplify Hub "signedOut" event this hook's own signOut() call
 * fires, and reacts with its OWN bare `<Navigate to="/login">` (no
 * wrong-portal state). Which of the two `<Navigate>` calls' state the
 * router actually keeps is a genuine, empirically-confirmed race (review
 * cycle 1: an earlier fix attempt here assumed making this hook's own
 * status synchronous — so its own `<Navigate state={{wrongPortalHome}}>`
 * commits in the SAME render that detects the mismatch, not gated behind
 * signOut()'s promise — would be enough to win that race. A live
 * regression test proved it isn't: `signOut()`'s Hub dispatch cascades
 * into PrivateRoute's OWN re-render and its own `<Navigate>`, and REACT
 * ROUTER's history.replace() calls don't merge state — the LAST one
 * applied simply overwrites the first, and that ordering isn't reliably
 * decidable from React's effect-firing order alone).
 *
 * The robust fix: don't rely on router state surviving the race at all.
 * The wrong-portal home is written to sessionStorage BEFORE signOut() is
 * called, so it's readable via `readAndClearWrongPortalHome()` regardless
 * of which `<Navigate>` call the router ends up keeping — LoginPage checks
 * router state first (works when THIS hook's own Navigate wins) and falls
 * back to the storage channel (works when PrivateRoute's bare one does).
 * The synchronous status return is kept anyway — it's still correct and
 * marginally reduces how often the fallback path is needed — but the
 * storage channel is what actually guarantees delivery.
 *
 * signOut() and clearing the query cache are still fire-and-forget side
 * effects here — same reasoning useLogout() already documents
 * (hooks/useAuth.ts) for why a forced sign-out must clear TanStack
 * Query's cache: this app runs on shared/kiosk iPads, and a wrong-portal
 * sign-out is exactly the kind of forced, non-consensual session end
 * where the NEXT person to use the device must not see the previous
 * account's cached data.
 *
 * Callers: frontend's RoleBasedRedirect/PortalBoundary, admin's
 * AdminBoundary, auditor's AuditorBoundary.
 */
export function usePortalGuard(
  home: PortalId | "no-access" | null,
  ownPortal: PortalId,
): PortalGuardStatus {
  const queryClient = useQueryClient();
  const isWrongPortal =
    home !== null && home !== "no-access" && home !== ownPortal;

  useEffect(() => {
    if (isWrongPortal) {
      try {
        sessionStorage.setItem(WRONG_PORTAL_STORAGE_KEY, home);
      } catch {
        // Storage unavailable — the router-state path still works when
        // this component's own <Navigate> happens to win the race.
      }
      queryClient.clear();
      // Shared device: this bounce stays in the same JS context (no hard
      // reload), so the captured logout-audit credentials must go with the
      // session they belong to.
      clearCapturedCredentials();
      void signOut();
    }
  }, [isWrongPortal, home, queryClient]);

  if (home === null) return "checking";
  if (home === ownPortal) return "own-portal";
  if (home === "no-access") return "no-access";
  return "wrong-portal";
}
